/* @HEADER
 * @version 1.1.1434 | 2026-11-01
 * @purpose Bounded data-testid registry for the ProjectSwitcher sidebar widget — stable hooks Playwright specs use to drive the project create/switch flow.
 * @sidecar project-switcher.testids.ts.header.md
 * @layer app | @hex _none_ | @ctx _none_
 * @public true
 * @edit careful
 */

// SpecRefs: ZVX-DEV-6252, ZVX-E2E-002, ZVX-DEV-8842

/** Bounded testid registry for the ProjectSwitcher (top-of-sidebar project selector + create flow). */
export const PROJECT_SWITCHER_TESTIDS = {
  trigger: 'project-switcher-trigger',
  dropdown: 'project-switcher-dropdown',
  /** ZVX-DEV-3632: the scrollable list region inside the dropdown (the section label, project rows, and hidden-projects section) — sits above the sticky "New Project" footer so a long list scrolls without hiding the create action. */
  list: 'project-switcher-list',
  /** ZVX-DEV-5457: the placeholder shown while ownership of at least one project is still unknown — see `projectListReadiness.ts`. */
  ownershipCheck: 'project-switcher-ownership-check',
  /** ZVX-DEV-5468: the one question no evidence can answer. Full ids: `-overlay`, ``, `-confirm`, `-cancel`, `-third`. */
  ownershipAsk: 'project-switcher-ownership-ask',
  newProjectBtn: 'project-switcher-new-project-btn',
  newProjectInput: 'project-switcher-new-project-input',
  /** Per-project entry inside the dropdown, keyed by project id (e.g. "default" or a UUID). */
  projectItem: (projectId: string) => `project-switcher-item-${projectId}`,
  /** Edit-mode name input (visible when rename pencil is clicked). */
  editNameInput: (projectId: string) => `project-switcher-edit-name-${projectId}`,
  /** Edit-mode key/prefix input (visible when rename pencil is clicked). */
  editPrefixInput: (projectId: string) => `project-switcher-edit-prefix-${projectId}`,
  /** Rename pencil button for a given project row. */
  renameBtn: (projectId: string) => `project-switcher-rename-btn-${projectId}`,
  /** ZVX-DEV-8842 (ZVX-5311 П1): the gear in a project's row that opens the project's settings panel. */
  settingsBtn: (projectId: string) => `project-switcher-settings-btn-${projectId}`,
  /** ZVX-DEV-8842: the project's settings panel (drawn on the body, one at a time). */
  settingsPanel: 'project-switcher-settings-panel',
  /** ZVX-DEV-8842: the section «Баги» inside the panel, drawn only for the open project. */
  settingsBugsSection: 'project-switcher-settings-bugs',
  /** ZVX-DEV-8842: the line that says to open the project first, and its button, for a project that is not the open one. */
  settingsOpenFirst: 'project-switcher-settings-open-first',
  settingsOpenBtn: 'project-switcher-settings-open-btn',
  /** ZVX-DEV-8842: the close button of the settings panel (the pointer's way to what Escape does). */
  settingsClose: 'project-switcher-settings-close',
  /** ZVX-DEV-3734: delete ("×") button for a given project row — arms the ConfirmDialog, previously only reachable via its `title` attribute. */
  deleteBtn: (projectId: string) => `project-switcher-delete-btn-${projectId}`,
  /** ZVX-DEV-2692: org selector shown in edit-mode (assign/move an existing project). */
  orgSelect: (projectId: string) => `project-switcher-org-select-${projectId}`,
  /** ZVX-DEV-6252 (D1): the «Не выводить в агрегатных досках» checkbox in edit-mode, per project. */
  hideFromAggregatesToggle: (projectId: string) => `project-switcher-hide-from-aggregates-${projectId}`,
  /** ZVX-DEV-2692: org selector shown in the new-project creation row. */
  /**
   * ZVX-DEV-5681 - the create form no longer OFFERS an organisation, it names
   * the one the project is being created in. Kept as a distinct id from the
   * retired select so a test asking for the chooser fails loudly instead of
   * finding a label and reading it as one.
   */
  newProjectOrgLabel: 'project-switcher-new-project-org-label',
  /** ZVX-DEV-2692: read-only badge on a project row showing its assigned org name, when assigned. */
  orgBadge: (projectId: string) => `project-switcher-org-badge-${projectId}`,
  /** ZVX-DEV-4757: shown when the active org holds no projects but others do — tells an empty org from an empty account. */
  emptyOrg: 'project-switcher-empty-org',
  /** ZVX-DEV-5473: the create-a-project button standing in place of the list when the active organization holds none. */
  emptyOrgCreate: 'project-switcher-empty-org-create',
  /** ZVX-DEV-2692: in-app confirm dialog shown before moving a project to a different org (replaces window.confirm). */
  orgConfirmDialogOverlay: 'project-switcher-org-confirm-overlay',
  orgConfirmDialog: 'project-switcher-org-confirm-dialog',
  orgConfirmDialogTitle: 'project-switcher-org-confirm-title',
  orgConfirmDialogBody: 'project-switcher-org-confirm-body',
  orgConfirmDialogCancelBtn: 'project-switcher-org-confirm-cancel-btn',
  orgConfirmDialogConfirmBtn: 'project-switcher-org-confirm-confirm-btn',
  /** ZVX-DEV-2745: full-screen overlay shown while an in-place project switch loads. */
  switchOverlay: 'project-switcher-switch-overlay',
  /** ZVX-DEV-8226: the banner «the project you had open was deleted on another device», held by the host so it survives the app's remount. */
  deletedElsewhereBanner: 'project-deleted-elsewhere-banner',
  deletedElsewhereTitle: 'project-deleted-elsewhere-title',
  /** Present only when the app moved to another project. */
  deletedElsewhereOpened: 'project-deleted-elsewhere-opened',
  /** Present only when the organisation has no other project visible to the person. */
  deletedElsewhereNoneLeft: 'project-deleted-elsewhere-none-left',
  deletedElsewhereDismiss: 'project-deleted-elsewhere-dismiss',
  // ZVX-DEV-8269 -- the same banner, told the person was removed from the organisation.
  leftOrganisationTitle: 'project-left-organisation-title',
  leftOrganisationBody: 'project-left-organisation-body',
  /**
   * ZVX-DEV-3520: span rendering the ACTIVE project's logo/icon inside the
   * trigger button. Carries `data-project-icon` (the raw `icon` field —
   * an emoji character, a `data:image/...` string, or '' when unset) so a
   * test can read back an avatar change without depending on which of the
   * two visual renderings (emoji text vs `<img>`) is currently active.
   */
  triggerLogo: 'project-switcher-trigger-logo',
  /**
   * ZVX-DEV-5473: the span naming the ACTIVE project inside the trigger. It
   * had no hook of its own, so what the switcher CLAIMS to be showing could
   * only be read through the whole button's text — which also carries the
   * logo initial and the arrow, and would have made an assertion on the name
   * pass on a label that merely contained it.
   */
  triggerName: 'project-switcher-trigger-name',
  /**
   * ZVX-DEV-3520: the per-row logo/icon span for a given project id — click
   * target that opens the EmojiPicker avatar popover for THAT row (works in
   * both edit-mode and plain display rows; `renderProjectLogo` mounts it
   * either way).
   */
  rowLogo: (projectId: string) => `project-switcher-row-logo-${projectId}`,
  /**
   * ZVX-DEV-3520: the read-only "ID-ключ карточек" prefix badge on a
   * project's dropdown row (previously untestid-addressable — only reachable
   * via its `title` attribute).
   */
  prefixBadge: (projectId: string) => `project-switcher-prefix-badge-${projectId}`,
  /**
   * ZVX-DEV-3734: base testid for the shared `ConfirmDialog` shown before
   * deleting a project — replaces the last remaining native
   * `window.confirm()` call site (the other nine sites were replaced under
   * ZVX-DEV-3633). `ConfirmDialog`'s `testId` prop derives
   * `${base}-overlay` / `${base}` / `${base}-confirm` / `${base}-cancel`.
   */
  deleteConfirmDialog: 'project-switcher-delete-confirm',
} as const;
